import React from "react";
import {
  FaCheck,
  FaEye,
  FaEyeSlash,
  FaLayerGroup,
  FaPowerOff,
} from "react-icons/fa";

import {
  DESCRIPTIONS,
  ROLE_META,
  ROLE_ORDER,
} from "../utils/modulesSettingsUtils";

function RoleSkeleton() {
  return (
    <section className="grid gap-5 xl:grid-cols-3">
      {ROLE_ORDER.map((roleId) => (
        <div
          key={roleId}
          className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900"
        >
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="space-y-3 p-4 sm:p-5">
            <div className="h-8 w-40 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800" />

            {[...Array(6)].map((_, index) => (
              <div
                key={index}
                className="h-20 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
              />
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

function EmptyRoleState({ roleLabel }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center dark:border-slate-800 dark:bg-slate-950/70">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400 dark:bg-slate-800">
        <FaLayerGroup />
      </div>

      <p className="mt-3 text-sm font-bold text-slate-800 dark:text-slate-200">
        Aucun module trouvé
      </p>

      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
        Aucun module ne correspond aux filtres pour le rôle {roleLabel}.
      </p>
    </div>
  );
}

function ModuleSwitch({ enabled, onClick, disabled, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={`Basculer ${label}`}
      onClick={onClick}
      disabled={disabled}
      className={`relative inline-flex h-7 w-14 shrink-0 cursor-pointer items-center rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 disabled:cursor-not-allowed disabled:opacity-50 ${
        enabled ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-700"
      }`}
    >
      <span
        className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition ${
          enabled ? "translate-x-7" : "translate-x-1"
        }`}
      />
    </button>
  );
}

function ModuleRow({ module, enabled, saving, onToggle }) {
  return (
    <article
      className={`rounded-2xl border p-4 transition ${
        enabled
          ? "border-emerald-200 bg-emerald-50/70 dark:border-emerald-500/20 dark:bg-emerald-500/10"
          : "border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950/50"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="font-bold text-slate-900 dark:text-white">
              {module.label}
            </h4>

            <span
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${
                enabled
                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
                  : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
              }`}
            >
              {enabled ? <FaEye /> : <FaEyeSlash />}
              {enabled ? "Affiché" : "Masqué"}
            </span>
          </div>

          <p className="mt-2 text-xs leading-5 text-slate-600 dark:text-slate-300">
            {DESCRIPTIONS[module.key] || "Module de l’application."}
          </p>

          <p className="mt-2 rounded-xl bg-white/70 px-2.5 py-1.5 font-mono text-[11px] text-slate-400 ring-1 ring-slate-200 dark:bg-slate-900/70 dark:ring-slate-800">
            {module.key}
          </p>
        </div>

        <ModuleSwitch
          enabled={enabled}
          onClick={() => onToggle(module.key)}
          disabled={saving}
          label={module.label}
        />
      </div>
    </article>
  );
}

function RoleCard({
  roleId,
  modules,
  settings,
  isEnabled,
  saving,
  onToggle,
  onEnableRole,
  onDisableRole,
}) {
  const meta = ROLE_META[roleId];
  const enabledCount = modules.filter((module) => isEnabled(module.key)).length;
  const total = modules.length;
  const percent = total ? Math.round((enabledCount / total) * 100) : 0;

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5">
        <div className="mb-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                Rôle
              </p>

              <h3 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
                {meta.label}
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold ring-1 ${meta.chip}`}
                >
                  {meta.short}
                </span>
              </h3>

              <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                {meta.description}
              </p>
            </div>
          </div>

          <div className="mt-4 rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-200 dark:bg-slate-950/70 dark:ring-slate-800">
            <div className="mb-2 flex items-center justify-between text-xs font-bold text-slate-600 dark:text-slate-300">
              <span>
                {enabledCount} / {total} activé(s)
              </span>
              <span>{percent}%</span>
            </div>

            <div className="h-3 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#013E8E] to-[#4CA82F] transition-all"
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>

          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => onEnableRole(roleId)}
              disabled={saving || !modules.length}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
            >
              <FaCheck />
              Tout activer
            </button>

            <button
              type="button"
              onClick={() => onDisableRole(roleId)}
              disabled={saving || !modules.length}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaPowerOff />
              Tout masquer
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {modules.length ? (
            modules.map((module) => (
              <ModuleRow
                key={`${roleId}-${module.key}`}
                module={module}
                enabled={isEnabled(module.key)}
                saving={saving}
                onToggle={onToggle}
              />
            ))
          ) : (
            <EmptyRoleState roleLabel={meta.label} />
          )}
        </div>
      </div>
    </section>
  );
}

export default function ModulesRoleGrid({
  loading,
  saving,
  settings,
  filteredRoleDefs,
  isEnabled,
  onToggle,
  onEnableRole,
  onDisableRole,
}) {
  if (loading) return <RoleSkeleton />;

  return (
    <section className="grid gap-5 xl:grid-cols-3">
      {ROLE_ORDER.map((roleId) => (
        <RoleCard
          key={roleId}
          roleId={roleId}
          modules={filteredRoleDefs?.[roleId] || []}
          settings={settings}
          isEnabled={isEnabled}
          saving={saving}
          onToggle={onToggle}
          onEnableRole={onEnableRole}
          onDisableRole={onDisableRole}
        />
      ))}
    </section>
  );
}